/**
 * DDFA Favorites Manager
 * Client-side wishlist using localStorage.
 * Dispatches 'favorites:updated' on window after every change.
 * Re-initializes header badges after cart-drawer section rendering.
 */
(() => {
  const STORAGE_KEY = 'ddfa_favorites';
  const EVENT_NAME = 'favorites:updated';
  const HEART_PATH = 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';

  let storageAvailable = true;
  try {
    localStorage.setItem('__test', '1');
    localStorage.removeItem('__test');
  } catch {
    storageAvailable = false;
    console.warn('[DDFA Favorites] localStorage is not available. Favorites will not persist.');
  }

  /* ── State management ── */

  function load() {
    if (!storageAvailable) return [];
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function save(handles) {
    if (!storageAvailable) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    dispatch(handles);
  }

  function dispatch(handles) {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: { handles, count: handles.length }
    }));
  }

  function has(handle) {
    return load().includes(handle);
  }

  function toggle(handle) {
    const handles = load();
    const idx = handles.indexOf(handle);
    if (idx > -1) {
      handles.splice(idx, 1);
    } else {
      handles.push(handle);
    }
    save(handles);
    return idx === -1; // true if added
  }

  function count() {
    return load().length;
  }

  function clear() {
    save([]);
  }

  /* ── UI updates ── */

  function updateButton(btn) {
    const handle = btn.getAttribute('data-product-handle');
    const isFav = has(handle);
    btn.setAttribute('aria-pressed', String(isFav));
    btn.setAttribute('aria-label',
      isFav ? 'Remove from favorites' : 'Add to favorites'
    );
    btn.classList.toggle('is-favorited', isFav);
  }

  function updateAllButtons() {
    document.querySelectorAll('[data-favorite-toggle]').forEach(updateButton);
  }

  function updateAllBadges() {
    const c = count();
    document.querySelectorAll('.ddfa-favorites-count').forEach(badge => {
      badge.textContent = c > 0 ? c : '';
      badge.style.display = c > 0 ? 'inline-block' : 'none';
    });
    updateHeaderHeartFill();
  }

  /* ── Header heart fill state ── */

  function updateHeaderHeartFill() {
    const c = count();
    // Desktop header heart
    document.querySelectorAll('.ddfa-header__action--favorites svg').forEach(svg => {
      if (c > 0) {
        svg.setAttribute('fill', '#ff6600');
        svg.style.stroke = '#ff6600';
      } else {
        svg.setAttribute('fill', 'none');
        svg.style.stroke = '';
      }
    });
    // Mobile toolbar heart — only target the default icon (not the hover icon)
    document.querySelectorAll('.ddfa-nav__toolbar-favorites .toolbar-icon-default').forEach(svg => {
      if (c > 0) {
        svg.setAttribute('fill', '#ff6600');
        svg.setAttribute('stroke', '#ff6600');
      } else {
        svg.setAttribute('fill', 'none');
        svg.setAttribute('stroke', 'currentColor');
      }
    });
  }

  /* ── Fly-to-header animation ── */

  function flyToHeader(sourceBtn) {
    // Find the visible header heart icon (desktop or mobile)
    const desktopHeart = document.querySelector('.ddfa-header__action--favorites');
    const mobileHeart = document.querySelector('.ddfa-nav__toolbar-favorites');
    // Pick whichever is visible
    let headerHeart = null;
    if (desktopHeart && desktopHeart.offsetParent !== null) {
      headerHeart = desktopHeart;
    } else if (mobileHeart && mobileHeart.offsetParent !== null) {
      headerHeart = mobileHeart;
    } else {
      headerHeart = desktopHeart || mobileHeart;
    }
    if (!headerHeart) return;

    // Get positions
    const srcRect = sourceBtn.getBoundingClientRect();
    const destRect = headerHeart.getBoundingClientRect();

    // Create flying heart element with light trail
    const flyer = document.createElement('div');
    flyer.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="${HEART_PATH}"/></svg>`;
    Object.assign(flyer.style, {
      position: 'fixed',
      zIndex: '99999',
      left: (srcRect.left + srcRect.width / 2) + 'px',
      top: (srcRect.top + srcRect.height / 2) + 'px',
      width: '24px',
      height: '24px',
      color: '#ff6600',
      pointerEvents: 'none',
      transform: 'translate(-50%, -50%)',
      transition: 'none',
      filter: 'drop-shadow(0 0 6px rgba(255, 140, 0, 0.8)) drop-shadow(0 0 12px rgba(255, 102, 0, 0.4))'
    });
    flyer.querySelector('svg').style.cssText = 'width:100%;height:100%;display:block;';
    document.body.appendChild(flyer);

    // Create trail particles that follow behind
    const trailCount = 5;
    const trails = [];
    for (let i = 0; i < trailCount; i++) {
      const trail = document.createElement('div');
      Object.assign(trail.style, {
        position: 'fixed',
        zIndex: '99998',
        left: (srcRect.left + srcRect.width / 2) + 'px',
        top: (srcRect.top + srcRect.height / 2) + 'px',
        width: (8 - i) + 'px',
        height: (8 - i) + 'px',
        borderRadius: '50%',
        background: `rgba(255, 140, 0, ${0.6 - i * 0.1})`,
        boxShadow: `0 0 ${6 - i}px rgba(255, 140, 0, ${0.4 - i * 0.06})`,
        pointerEvents: 'none',
        transform: 'translate(-50%, -50%)',
        transition: 'none'
      });
      document.body.appendChild(trail);
      trails.push(trail);
    }

    // Calculate flight path
    const dx = destRect.left + destRect.width / 2 - (srcRect.left + srcRect.width / 2);
    const dy = destRect.top + destRect.height / 2 - (srcRect.top + srcRect.height / 2);

    // Animate with arc trajectory — slower and more visible
    const anim = flyer.animate([
      {
        transform: 'translate(-50%, -50%) scale(1)',
        opacity: 1
      },
      {
        transform: `translate(calc(-50% + ${dx * 0.3}px), calc(-50% + ${dy * 0.3 - 50}px)) scale(1.1)`,
        opacity: 1,
        offset: 0.3
      },
      {
        transform: `translate(calc(-50% + ${dx * 0.6}px), calc(-50% + ${dy * 0.6 - 30}px)) scale(0.8)`,
        opacity: 0.9,
        offset: 0.6
      },
      {
        transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.3)`,
        opacity: 0.6
      }
    ], {
      duration: 900,
      easing: 'cubic-bezier(0.2, 0.6, 0.3, 1)',
      fill: 'forwards'
    });

    // Animate trail particles with staggered delay
    trails.forEach((trail, i) => {
      const delay = (i + 1) * 60;
      const trailAnim = trail.animate([
        {
          transform: 'translate(-50%, -50%) scale(1)',
          opacity: 0.6 - i * 0.1
        },
        {
          transform: `translate(calc(-50% + ${dx * 0.3}px), calc(-50% + ${dy * 0.3 - 50}px)) scale(0.8)`,
          opacity: 0.4 - i * 0.06,
          offset: 0.3
        },
        {
          transform: `translate(calc(-50% + ${dx * 0.6}px), calc(-50% + ${dy * 0.6 - 30}px)) scale(0.5)`,
          opacity: 0.2,
          offset: 0.6
        },
        {
          transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0)`,
          opacity: 0
        }
      ], {
        duration: 900,
        delay: delay,
        easing: 'cubic-bezier(0.2, 0.6, 0.3, 1)',
        fill: 'forwards'
      });
      trailAnim.onfinish = () => trail.remove();
    });

    anim.onfinish = () => {
      flyer.remove();
      pulseHeaderHeart(headerHeart);
    };
  }

  function pulseHeaderHeart(headerEl) {
    const svg = headerEl.querySelector('svg') || headerEl.querySelector('.toolbar-icon-default');
    if (!svg) return;
    svg.animate([
      { transform: 'scale(1)' },
      { transform: 'scale(1.4)' },
      { transform: 'scale(1)' }
    ], { duration: 300, easing: 'ease' });
  }

  /* ── Event delegation (single listener on body) ── */

  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-favorite-toggle]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    const wasAdded = toggle(handle);
    updateButton(btn);

    if (wasAdded) {
      // Fly heart to header animation
      flyToHeader(btn);
    } else {
      // Pulse animation for removal
      btn.classList.remove('favorite-pulse');
      void btn.offsetWidth;
      btn.classList.add('favorite-pulse');
      btn.addEventListener('animationend', () => {
        btn.classList.remove('favorite-pulse');
      }, { once: true });
    }
  });

  /* ── React to changes from any source ── */

  window.addEventListener(EVENT_NAME, () => {
    updateAllButtons();
    updateAllBadges();
  });

  /* ── Re-init after cart drawer replaces header HTML ── */

  window.addEventListener('ddfa:cart:changed', () => {
    updateAllBadges();
  });

  /* ── Cross-tab synchronization ── */

  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      updateAllButtons();
      updateAllBadges();
    }
  });

  /* ── Initialize ── */

  function init() {
    updateAllButtons();
    updateAllBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Public API ── */

  window.ddfaFavorites = { load, has, toggle, count, clear, updateAllButtons, updateAllBadges };
})();
